import { useState, useEffect, useCallback } from "react";
import { Modal } from "antd";
import { useOrderingMessage } from "../../useOrderingMessage";
import { useTranslation } from "react-i18next";
import { posModalStyles as modalStyles } from "../../modalPrimitives";
import {
  PlusOutlined,
  MinusOutlined,
  ScissorOutlined,
  CheckOutlined,
  BarsOutlined,
  PieChartOutlined,
} from "@ant-design/icons";
import { useMenuItems, useMenuModifiers } from "../../../../../hooks/useMenu";
import { useSplitBill } from "../../../../../hooks/useBills";
import { getApiErrorMessage } from "../../../../../utils/translations";
import { useAppSelector } from "../../../../../store/hooks";
import { NewTableModal } from "../manage/NewTableModal";
import { ConfirmActionModal } from "../ConfirmActionModal";
import { TransferWaiterModal } from "../manage/TransferWaiterModal";
import { SplitAndPayModal } from "../payment/SplitAndPayModal";
import type { BillOut } from "../../../../../api/bills";
import { useViewport } from "../../../../../hooks/useViewport";
import {
  ModalHeader,
  ModalFooter,
  GhostButton,
  PrimaryButton,
  SoftPrimaryButton,
} from "../../modalPrimitives";

interface SplitBillModalProps {
  open: boolean;
  onClose: () => void;
  billId: number;
  bill: BillOut | undefined;
}

interface SelectedQuantities {
  [billElementItemId: number]: number;
}

export const SplitBillModal = ({
  open,
  onClose,
  billId,
  bill,
}: SplitBillModalProps) => {
  const { t, i18n } = useTranslation();
  const { isMobile, isNarrow, isTablet, isDesktop } = useViewport();
  const allItems = useMenuItems();
  const allModifiers = useMenuModifiers();
  const splitBillMutation = useSplitBill();
  const message = useOrderingMessage();
  const permissions = useAppSelector((state) => state.auth.permissions);
  const canTransferToOperator = permissions.some(
    (p) => p.name === "split_bill_to_operator",
  );
  const canPaymentCash = permissions.some((p) => p.name === "payment_cash");
  const canPaymentCard = permissions.some((p) => p.name === "payment_card");
  const canCloseBill1 = permissions.some((p) => p.name === "close_bill_1");
  const [selectedQuantities, setSelectedQuantities] =
    useState<SelectedQuantities>({});
  // „По продукти" = ръчен избор; „По равно" = половината от всеки ред е
  // предварително избрана (сметката се дели на две части).
  const [splitMode, setSplitMode] = useState<"products" | "equal">("products");
  const [showTableNameModal, setShowTableNameModal] = useState(false);
  const [showWaiterModal, setShowWaiterModal] = useState(false);
  const [showSplitAndPayModal, setShowSplitAndPayModal] = useState(false);
  const [showConfirmModal, setShowConfirmModal] = useState(false);
  const [confirmMessage, setConfirmMessage] = useState("");
  const [pendingTableName, setPendingTableName] = useState("");
  const [pendingWaiterId, setPendingWaiterId] = useState<number | null>(null);
  // Името на заета маса на ДРУГ сервитьор при липсващо право
  // split_bill_to_operator — показва информативен модал без опция за сливане.
  const [occupiedByOtherName, setOccupiedByOtherName] = useState<string | null>(
    null,
  );

  const getTranslation = (
    translations: Array<{ languageCode: string; name: string }>,
    code = i18n.language,
  ) => translations.find((tr) => tr.languageCode === code)?.name || "";

  const billItems =
    bill?.elements.flatMap((el) =>
      el.items.map((item) => ({
        ...item,
        billElementId: el.id,
        comment: el.comment,
      })),
    ) || [];

  const handleQuantityChange = (itemId: number, delta: number) => {
    const item = billItems.find((i) => i.id === itemId);
    if (!item) return;
    const maxQty = parseFloat(item.quantity);
    const currentQty = selectedQuantities[itemId] || 0;
    const newQty = Math.max(0, Math.min(maxQty, currentQty + delta));
    setSelectedQuantities((prev) => ({ ...prev, [itemId]: newQty }));
  };

  const handleModeChange = (mode: "products" | "equal") => {
    setSplitMode(mode);
    if (mode === "equal") {
      setSelectedQuantities(
        Object.fromEntries(
          billItems.map((item) => [item.id, parseFloat(item.quantity) / 2]),
        ),
      );
    } else {
      setSelectedQuantities({});
    }
  };

  const selectedItems = billItems.filter(
    (item) => (selectedQuantities[item.id] || 0) > 0,
  );

  // „Избери всичко" — изведено от селекцията (както при сторно): маха ли се
  // количество от ред, чекбоксът сам угасва. Повторно натискане чисти всичко.
  const allSelected =
    billItems.length > 0 &&
    billItems.every(
      (item) => (selectedQuantities[item.id] || 0) >= parseFloat(item.quantity),
    );

  const handleToggleSelectAll = () => {
    if (allSelected) {
      setSelectedQuantities({});
    } else {
      setSelectedQuantities(
        Object.fromEntries(
          billItems.map((item) => [item.id, parseFloat(item.quantity)]),
        ),
      );
    }
  };

  // Цена на ред (продукт + добавки) — добавките не са в item.total_sum.
  // Крайните им суми се четат от самите тях: отстъпката върху продукт ги
  // обхваща (BE ги преизчислява), промяната на цената — не.
  const getLineTotal = (item: (typeof billItems)[number]) => {
    const additionsTotal = item.additions.reduce(
      (sum, a) => sum + parseFloat(a.total_sum),
      0,
    );
    return parseFloat(item.total_sum) + additionsTotal;
  };

  const totalSelectedAmount = selectedItems.reduce((sum, item) => {
    const qty = selectedQuantities[item.id] || 0;
    const maxQty = parseFloat(item.quantity);
    return sum + (getLineTotal(item) / maxQty) * qty;
  }, 0);

  const submitSplit = async (
    newTableName: string,
    waiterId: number | null,
    forceExisting = false,
  ) => {
    const elements = selectedItems.map((item) => ({
      billElementId: item.id,
      quantity: selectedQuantities[item.id],
    }));
    try {
      await splitBillMutation.mutateAsync({
        billId,
        data: {
          splitTableName: newTableName,
          comment: null,
          elements,
          ...(forceExisting && { splitInExistingBill: true }),
          ...(waiterId !== null && { targetWaiterId: waiterId }),
        },
      });
      setSelectedQuantities({});
      setSplitMode("products");
      setShowTableNameModal(false);
      setShowWaiterModal(false);
      setShowConfirmModal(false);
      setPendingTableName("");
      setPendingWaiterId(null);
      onClose();
    } catch (error: unknown) {
      console.error("Split bill error:", error);
      const detail =
        (error as { response?: { data?: { detail?: string } } })?.response
          ?.data?.detail;
      const errorMessage = getApiErrorMessage(error, t("splitBill.splitError"));
      // Маса на ДРУГ сервитьор без право split_bill_to_operator: сливането е
      // забранено от BE — информираме, без въпрос за прехвърляне.
      if (
        typeof detail === "string" &&
        detail.includes("заета от друг сервитьор")
      ) {
        setShowTableNameModal(false);
        setShowWaiterModal(false);
        setPendingTableName("");
        setPendingWaiterId(null);
        setOccupiedByOtherName(newTableName);
      } else if (
        !forceExisting &&
        typeof detail === "string" &&
        detail.includes("вече съществува и е отворена")
      ) {
        setPendingTableName(newTableName);
        setPendingWaiterId(waiterId);
        setConfirmMessage(errorMessage);
        setShowTableNameModal(false);
        setShowWaiterModal(false);
        setShowConfirmModal(true);
      } else {
        message.error(errorMessage);
      }
    }
  };

  // Стъпка 1 → след въвеждане на име на маса:
  //  — ако потребителят има split_bill_to_operator → отваряме избор на сервитьор
  //  — иначе пропускаме стъпката и прехвърляме към същия сервитьор
  const handleTableNameSubmit = (newTableName: string) => {
    setPendingTableName(newTableName);
    setShowTableNameModal(false);
    if (canTransferToOperator) {
      setShowWaiterModal(true);
    } else {
      submitSplit(newTableName, null, false);
    }
  };

  // Стъпка 2 → избор на сервитьор. Waiter модалът остава отворен
  // докато API-то не успее — затварянето става в submitSplit на success.
  const handleWaiterSubmit = (waiterId: number | null) => {
    setPendingWaiterId(waiterId);
    submitSplit(pendingTableName, waiterId, false);
  };

  const handleConfirmTransfer = () =>
    submitSplit(pendingTableName, pendingWaiterId, true);
  const handleCancelTransfer = () => {
    setShowConfirmModal(false);
    setPendingTableName("");
    setPendingWaiterId(null);
    setConfirmMessage("");
  };

  const handleClose = useCallback(() => {
    setSelectedQuantities({});
    setSplitMode("products");
    setShowTableNameModal(false);
    setShowWaiterModal(false);
    setShowSplitAndPayModal(false);
    setShowConfirmModal(false);
    setPendingTableName("");
    setPendingWaiterId(null);
    setConfirmMessage("");
    setOccupiedByOtherName(null);
    onClose();
  }, [onClose]);

  useEffect(() => {
    if (!open || !isDesktop) return;
    const handleKeyDown = (e: KeyboardEvent) => {
      if (
        showTableNameModal ||
        showWaiterModal ||
        showSplitAndPayModal ||
        showConfirmModal ||
        occupiedByOtherName !== null
      )
        return;
      if (e.key === "Enter") {
        e.preventDefault();
        if (selectedItems.length > 0) setShowTableNameModal(true);
      } else if (e.key === "Escape") {
        e.preventDefault();
        handleClose();
      }
    };
    window.addEventListener("keydown", handleKeyDown);
    return () => window.removeEventListener("keydown", handleKeyDown);
  }, [
    open,
    selectedItems,
    showTableNameModal,
    showWaiterModal,
    showSplitAndPayModal,
    showConfirmModal,
    occupiedByOtherName,
    handleClose,
    isDesktop,
  ]);

  const width = isMobile ? undefined : isTablet ? 520 : 640;
  const bodyPadClass = isNarrow ? "px-4" : "px-6";
  const billTotal = parseFloat(bill?.total_amount ?? "0");

  return (
    <>
      <Modal
        open={open}
        onCancel={handleClose}
        footer={null}
        centered
        closable={false}
        destroyOnHidden
        width={width}
        styles={modalStyles}
        style={{ maxWidth: "calc(100vw - 32px)" }}
      >
        <div className="flex max-h-[85vh] flex-col bg-pos-card text-pos-ink">
          <ModalHeader
            icon={<ScissorOutlined />}
            title={t("splitBill.title")}
            subtitle={t("splitBill.subtitle", {
              name: bill?.name ?? "",
              total: billTotal.toFixed(2),
            })}
            onClose={handleClose}
            isNarrow={isNarrow}
          />

          {/* Режим: по продукти (ръчен избор) / по равно (половината от всеки ред) */}
          <div
            className={`${bodyPadClass} flex gap-2.5 pt-3.5 pb-2.5 ${
              isNarrow
                ? "flex-col items-center"
                : "items-center justify-between"
            }`}
          >
            {/* Същият сегментиран превключвател като „Плочки | Таблица" в OrderFooter */}
            <span className="inline-flex h-10 shrink-0 items-stretch overflow-hidden rounded-[11px] border border-pos-ctl bg-pos-card">
              <SegmentButton
                icon={<BarsOutlined className="text-[15px]" />}
                label={t("splitBill.modeByProducts")}
                active={splitMode === "products"}
                onClick={() => handleModeChange("products")}
              />
              <SegmentButton
                icon={<PieChartOutlined className="text-[15px]" />}
                label={t("splitBill.modeEqual")}
                active={splitMode === "equal"}
                onClick={() => handleModeChange("equal")}
                divider
              />
            </span>
            {/* Същият стил като подзаглавието в хедъра */}
            <span className="truncate text-[13px] font-medium leading-normal text-pos-muted">
              {t("splitBill.selectedProducts", {
                count: selectedItems.length,
              })}
            </span>
          </div>

          <div
            className={`${bodyPadClass} flex min-h-0 flex-col gap-2 overflow-y-auto pb-4`}
          >
            {billItems.map((item) => {
              const itemName =
                item.item_names?.[i18n.language] ||
                item.item_names?.["bg"] ||
                Object.values(item.item_names || {})[0] ||
                t("common.product");
              const maxQty = parseFloat(item.quantity);
              const selectedQty = selectedQuantities[item.id] || 0;
              const unitPrice = getLineTotal(item) / parseFloat(item.quantity);
              const active = selectedQty > 0;

              return (
                <div
                  key={item.id}
                  className={`rounded-[10px] border px-3 py-2.5 transition-colors ${
                    active
                      ? "border-pos-accent/30 bg-pos-marked"
                      : "border-pos-line3 bg-pos-panel"
                  }`}
                >
                  <div className="flex items-center justify-between gap-2.5">
                    <div className="flex min-w-0 flex-1 items-baseline gap-2">
                      <span className="min-w-0 truncate text-[13.5px] font-semibold text-pos-ink">
                        {itemName}
                      </span>
                      <span className="shrink-0 font-pos-mono text-[11.5px] text-pos-muted">
                        {unitPrice.toFixed(2)}
                      </span>
                    </div>

                    <div className="flex shrink-0 items-center gap-1">
                      <QtyBtn
                        icon={<MinusOutlined />}
                        onClick={() => handleQuantityChange(item.id, -1)}
                        disabled={selectedQty === 0}
                      />
                      {/* Избраното количество, а под него „от N" — като в „Сторно" */}
                      <div className="flex min-w-[48px] flex-col items-center leading-none">
                        <span
                          className={`font-pos-mono text-[15px] font-bold tabular-nums ${
                            active ? "text-pos-accent-ink" : "text-pos-ink"
                          }`}
                        >
                          {selectedQty}
                        </span>
                        <span className="mt-0.5 font-pos-mono text-[10px] text-pos-muted">
                          {t("cancelItem.ofQuantity", { max: maxQty })}
                        </span>
                      </div>
                      <QtyBtn
                        icon={<PlusOutlined />}
                        onClick={() => handleQuantityChange(item.id, 1)}
                        disabled={selectedQty >= maxQty}
                      />
                    </div>
                  </div>

                  {(item.modifiers.length > 0 || item.additions.length > 0) && (
                    <div className="mt-1.5 ml-2 flex flex-col gap-0.5 border-l-2 border-pos-line pl-2.5 text-[11px] text-pos-soft">
                      {item.modifiers.length > 0 && (
                        <div>
                          {t("splitBill.modifiersLabel")}{" "}
                          {item.modifiers
                            .map((m) => {
                              const mod = allModifiers.find(
                                (modifier) =>
                                  modifier.id.toString() === m.modifier_id,
                              );
                              return mod
                                ? getTranslation(mod.translations)
                                : "";
                            })
                            .filter(Boolean)
                            .join(", ")}
                        </div>
                      )}
                      {item.additions.length > 0 && (
                        <div>
                          {t("splitBill.addonsLabel")}{" "}
                          {item.additions
                            .map((a) => {
                              const addonItem = allItems.find(
                                (i) => i.id === a.addition_item_id,
                              );
                              return addonItem
                                ? `${getTranslation(addonItem.itemLangs)} x${a.quantity}`
                                : "";
                            })
                            .filter(Boolean)
                            .join(", ")}
                        </div>
                      )}
                    </div>
                  )}
                </div>
              );
            })}
          </div>

          {/* Обобщение: „Избери всичко" (бутон) вляво, брой избрани + сума вдясно */}
          <div
            className={`flex items-center justify-between gap-2.5 border-t border-pos-line2 ${
              isNarrow ? "px-4 py-2.5" : "px-6 py-3"
            }`}
          >
            <SelectAllButton
              checked={allSelected}
              onToggle={handleToggleSelectAll}
              label={t("splitBill.selectAll")}
            />
            <div className="flex min-w-0 items-baseline gap-2">
              <span className="text-[13px] font-semibold text-pos-soft2">
                {t("splitBill.sumLabel")}
              </span>
              <span className="font-pos-mono text-lg font-bold tabular-nums text-pos-accent-ink">
                {totalSelectedAmount.toFixed(2)}
              </span>
            </div>
          </div>

          <ModalFooter isNarrow={isNarrow} columns="1fr 2fr 2fr">
            <GhostButton
              label={t("splitBill.cancelButton")}
              onClick={handleClose}
              shortcut={isDesktop ? "Esc" : undefined}
            />
            <PrimaryButton
              label={t("splitBill.splitButton")}
              onClick={() => setShowTableNameModal(true)}
              disabled={selectedItems.length === 0}
              shortcut={isDesktop ? "⏎" : undefined}
            />
            <SoftPrimaryButton
              label={t("splitBill.splitAndPayButton")}
              onClick={() => setShowSplitAndPayModal(true)}
              disabled={selectedItems.length === 0}
            />
          </ModalFooter>
        </div>
      </Modal>

      <NewTableModal
        open={showTableNameModal}
        onClose={() => setShowTableNameModal(false)}
        onSubmit={handleTableNameSubmit}
        allowExistingName
      />

      <TransferWaiterModal
        open={showWaiterModal}
        onBack={() => {
          // Назад към избора на продукти — нищо не се изпраща към backend
          setShowWaiterModal(false);
          setShowTableNameModal(false);
          setPendingTableName("");
          setPendingWaiterId(null);
        }}
        onSubmit={handleWaiterSubmit}
        currentWaiterId={bill?.waiter_id ?? null}
        submitting={splitBillMutation.isPending}
        userRole="ordering"
        transferAmount={totalSelectedAmount}
      />

      <ConfirmActionModal
        open={showConfirmModal}
        title={t("confirmSplit.title")}
        message={confirmMessage}
        question={t("confirmSplit.transferQuestion")}
        confirmLabel={t("confirmSplit.transferButton")}
        onCancel={handleCancelTransfer}
        onConfirm={handleConfirmTransfer}
      />

      {/* Заета маса на друг сервитьор без право split_bill_to_operator —
          само информация, няма опция за сливане (BE-то го забранява). */}
      <ConfirmActionModal
        open={occupiedByOtherName !== null}
        title={t("confirmSplit.title")}
        message={t("confirmSplit.occupiedByOtherWaiter", {
          name: occupiedByOtherName ?? "",
        })}
        question={t("confirmSplit.noTransferPermission")}
        cancelLabel={t("confirmSplit.understoodButton")}
        onCancel={() => setOccupiedByOtherName(null)}
      />

      <SplitAndPayModal
        open={showSplitAndPayModal}
        onClose={() => setShowSplitAndPayModal(false)}
        onSuccess={() => {
          setShowSplitAndPayModal(false);
          setSelectedQuantities({});
          setSplitMode("products");
          onClose();
        }}
        billId={billId}
        sourceTableName={bill?.name ?? ""}
        elements={selectedItems.map((item) => ({
          billElementId: item.id,
          quantity: selectedQuantities[item.id],
        }))}
        totalAmount={totalSelectedAmount}
        canPaymentCash={canPaymentCash}
        canPaymentCard={canPaymentCard}
        canCloseBill1={canCloseBill1}
        customerId={bill?.customer_id ?? null}
      />
    </>
  );
};

// „Избери всичко" — бутон с чекбокс-квадратче вътре (по-лесен за натискане);
// самото квадратче светва в accent при активно, бутонът остава неутрален.
const SelectAllButton = ({
  checked,
  onToggle,
  label,
}: {
  checked: boolean;
  onToggle: () => void;
  label: string;
}) => (
  <button
    type="button"
    onClick={onToggle}
    className="inline-flex h-9 shrink-0 cursor-pointer items-center gap-2 rounded-[10px] border border-pos-ctl bg-pos-card px-3 text-[13px] font-semibold text-pos-ink2 transition-colors hover:bg-pos-canvas"
  >
    {/* Квадратче като чекбокс — светва в accent при активно */}
    <span
      className={`inline-flex h-[18px] w-[18px] shrink-0 items-center justify-center rounded-[5px] border text-white transition-colors ${
        checked ? "border-pos-accent bg-pos-accent" : "border-pos-check bg-pos-canvas"
      }`}
    >
      {checked && <CheckOutlined className="text-[10px]" />}
    </span>
    <span>{label}</span>
  </button>
);

// Сегмент от превключвателя на режима („По продукти" / „По равно") — класовете
// са тези на `segment()` в menu/OrderFooter.tsx (изглед плочки/таблица).
const SegmentButton = ({
  icon,
  label,
  active,
  onClick,
  divider,
}: {
  icon: React.ReactNode;
  label: string;
  active: boolean;
  onClick: () => void;
  divider?: boolean;
}) => (
  <button
    type="button"
    onClick={onClick}
    aria-pressed={active}
    className={`inline-flex cursor-pointer items-center gap-1.5 px-3.5 text-[13px] font-semibold transition-colors ${
      divider ? "border-l border-pos-ctl" : ""
    } ${
      active
        ? "bg-pos-marked text-pos-accent-ink"
        : "text-pos-ink2 hover:bg-pos-panel"
    }`}
  >
    {icon}
    {label}
  </button>
);

const QtyBtn = ({
  icon,
  onClick,
  disabled,
}: {
  icon: React.ReactNode;
  onClick: () => void;
  disabled?: boolean;
}) => (
  // Tailwind hover: (v4) важи само на устройства с истински hover — на телефон
  // докосването НЕ оцветява бутона (JS onMouseEnter „залепваше" фона след tap)
  <button
    type="button"
    onClick={onClick}
    disabled={disabled}
    className="w-[30px] h-[30px] rounded-lg bg-pos-card border border-pos-ctl text-pos-soft2 inline-flex items-center justify-center text-xs cursor-pointer transition-colors disabled:cursor-not-allowed disabled:opacity-40 enabled:hover:bg-pos-marked enabled:hover:border-pos-accent/30 enabled:hover:text-pos-accent-ink"
  >
    {icon}
  </button>
);
